import {
  IconName,
  PositionUpdateSourceEnum,
  isTalosUser,
  removeEmptyFilters,
  useAssetsFilter,
  type FilterClause,
  type FilterableProperty,
  type UseFilterBuilderProps,
} from '@talos/kyoko';
import { compact, isEqual, startCase } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useUser } from '../../../../hooks';
import { useDerivativeExpiriesFilter } from '../../../../hooks/filters/useDerivativeExpiriesFilter';
import { useMarketAccountGroupsFilter } from '../../../../hooks/filters/useMarketAccountGroupsFilter';
import { useMarketAccountsFilter } from '../../../../hooks/filters/useMarketAccountsFilter';
import type { PositionsTableFilter } from '../types';
import { usePositionsBaseFilter, type UsePositionsBaseFilterParams } from '../usePositionsBaseFilter';

interface UsePositionsUnifiedFilterParams extends UsePositionsBaseFilterParams {
  defaultFilter: PositionsTableFilter;
}

export const usePositionsUnifiedFilter = ({ defaultFilter, ...params }: UsePositionsUnifiedFilterParams) => {
  const user = useUser();
  const baseFilter = usePositionsBaseFilter(params);
  const assetsFilter = useAssetsFilter();
  const expiriesFilter = useDerivativeExpiriesFilter();
  const accountsFilter = useMarketAccountsFilter();
  const marketAccountGroupsFilter = useMarketAccountGroupsFilter();

  const filterableProperties = useMemo(
    () =>
      compact<FilterableProperty<keyof PositionsTableFilter>>([
        { ...assetsFilter, label: 'Instrument' },
        accountsFilter,
        expiriesFilter,
        marketAccountGroupsFilter,
        isTalosUser(user) && {
          key: 'Source',
          label: 'Source',
          icon: IconName.CubeTransparent,
          control: 'single-select',
          options: Object.keys(PositionUpdateSourceEnum),
          getOptionLabel: (option: string) => startCase(option),
        },
      ]),
    [assetsFilter, accountsFilter, expiriesFilter, marketAccountGroupsFilter, user]
  );

  const { changeFilter, initialFilterClauses } = baseFilter;

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter = removeEmptyFilters<PositionsTableFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) satisfies PositionsTableFilter),
          Source: isTalosUser(user)
            ? (filterClausesByPropertyKey.get('Source')?.selections?.[0] as PositionUpdateSourceEnum)
            : PositionUpdateSourceEnum.Gateway,
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter, user]
  );

  const filterBuilderProps = useMemo(
    () =>
      ({
        initialFilterClauses,
        properties: filterableProperties,
        onFilterClausesChanged: handleFilterClausesChanged,
      }) satisfies UseFilterBuilderProps,
    [initialFilterClauses, filterableProperties, handleFilterClausesChanged]
  );

  return {
    filterBuilderProps,
    ...baseFilter,
  };
};
